import React, { useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/joy";
import CustomSwitch from "components/common/CustomSwitch";
import { theme } from "styles/theme";
import type { TaskEditorState } from "./utils/taskEditorUtils";
import {
  DiffCodeLines,
  JsonCodeBox,
  JSON_CODE_PRE_SX,
  diffOrderedJson,
  orderedStringify,
} from "./jsonCodeView";

interface TaskEditorCodeViewProps {
  task: TaskEditorState;
  // 마지막 저장 상태. 현재 blocks 와 비교해 diff 를 만든다.
  v1Task?: TaskEditorState;
}

export default function TaskEditorCodeView({
  task,
  v1Task,
}: TaskEditorCodeViewProps) {
  const [showDiff, setShowDiff] = useState(false);

  // 변경 내용 diff 와 동일하게 메타데이터 제외, blocks 만 표시.
  const blocksJson = useMemo(
    () => orderedStringify(task.blocks),
    [task.blocks]
  );

  const diffLines = useMemo(
    () => (v1Task ? diffOrderedJson(v1Task.blocks, task.blocks) : []),
    [v1Task, task]
  );

  const hasDiff = useMemo(
    () => diffLines.some((line) => line.kind !== "same"),
    [diffLines]
  );

  return (
    <Stack spacing={1} sx={{ height: "100%", minHeight: 0 }}>
      {/* minHeight 26px 로 미리보기 체험모드 토글 행과 같은 높이 유지 */}
      <Stack
        direction="row"
        spacing="6px"
        justifyContent="center"
        sx={{ alignItems: "center", flexShrink: 0, minHeight: "26px" }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: theme.vars.palette.Content.Weak,
          }}
        >
          Diff 모드
        </Typography>
        <CustomSwitch
          checked={showDiff}
          onChange={(event) => setShowDiff(event.target.checked)}
          slotProps={{ input: { "aria-label": "Diff 모드" } }}
        />
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        {showDiff ? (
          hasDiff ? (
            <Box component="pre" sx={JSON_CODE_PRE_SX}>
              <DiffCodeLines lines={diffLines} />
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                borderColor: theme.vars.palette.Border.Weak,
                borderRadius: "8px",
                backgroundColor: theme.vars.palette.Surface.Forward,
              }}
            >
              <Typography
                variant={"Caption"}
                sx={{ color: theme.vars.palette.Content.Weak }}
              >
                저장된 내용과 달라진 점이 없어요
              </Typography>
            </Box>
          )
        ) : (
          <JsonCodeBox json={blocksJson} />
        )}
      </Box>
    </Stack>
  );
}
